/**
 * Сведения о скорости доски для отчёта «Проблема».
 *
 * У одних доска летает, у других на той же доске всё тормозит. Тяжёлое
 * место — не код доски, а прорисовка тысяч штрихов браузером, и упирается
 * она в видеокарту и процессор человека. Чтобы не гадать, отчёт несёт:
 * видеокарту (или признак, что её нет и всё рисует процессор), ядра,
 * память, размер экрана, сколько объектов рисуется, сколько длится
 * перерисовка и сколько было подвисаний за последние минуты.
 *
 * Подвисания ловим наблюдателем браузера — он ничего не стоит, пока
 * подвисаний нет. Свой цикл кадров не крутим: он сам ел бы батарею.
 */

type Stall = { at: number; ms: number };

const STALL_WINDOW_MS = 3 * 60 * 1000;
const MAX_STALLS = 200;
const stalls: Stall[] = [];
const renders: number[] = [];
let installed = false;

function noteStall(ms: number): void {
  stalls.push({ at: performance.now(), ms });
  if (stalls.length > MAX_STALLS) stalls.shift();
}

export function installPerfProbe(): void {
  if (installed || typeof PerformanceObserver === "undefined") return;
  installed = true;
  const types = PerformanceObserver.supportedEntryTypes ?? [];
  // long-animation-frame точнее (кадр целиком, с прорисовкой), longtask — запасной.
  const type = types.includes("long-animation-frame")
    ? "long-animation-frame"
    : types.includes("longtask")
      ? "longtask"
      : null;
  if (!type) return;
  try {
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) noteStall(e.duration);
    }).observe({ type, buffered: true });
  } catch {
    /* браузер не умеет — отчёт обойдётся без подвисаний */
  }
}

/** Сцена сообщает, сколько длилась её перерисовка. */
export function noteSceneRender(ms: number): void {
  renders.push(ms);
  if (renders.length > 30) renders.shift();
}

function gpuName(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl")) as
      | WebGLRenderingContext
      | null;
    if (!gl) return "нет WebGL (рисует процессор)";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const name = ext
      ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL))
      : String(gl.getParameter(gl.RENDERER));
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    // Программная отрисовка: видеокарта выключена или не поддерживается.
    const software = /swiftshader|llvmpipe|basic render|software/i.test(name);
    return software ? `${name} — ПРОГРАММНАЯ, без видеокарты` : name;
  } catch {
    return "не удалось узнать";
  }
}

function percentile(values: number[], p: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))]!;
}

/** Строки для отчёта. */
export function perfDiagnosticsLines(): string[] {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const lines: string[] = [];
  lines.push(`gpu: ${gpuName()}`);
  lines.push(
    `cpu_cores: ${nav.hardwareConcurrency ?? "?"}, memory_gb: ${nav.deviceMemory ?? "?"}`
  );
  lines.push(
    `screen: ${window.screen.width}x${window.screen.height} @${window.devicePixelRatio}x`
  );
  const stats = (window as Window & { __wbRenderStats?: Record<string, unknown> })
    .__wbRenderStats;
  if (stats) lines.push(`render_stats: ${JSON.stringify(stats)}`);
  if (renders.length) {
    lines.push(
      `render_ms: последняя ${Math.round(renders[renders.length - 1]!)}, медиана ${Math.round(
        percentile(renders, 0.5)
      )}, худшая ${Math.round(Math.max(...renders))} (из ${renders.length})`
    );
  }
  const now = performance.now();
  const recent = stalls.filter((s) => now - s.at <= STALL_WINDOW_MS);
  if (recent.length) {
    const total = recent.reduce((sum, s) => sum + s.ms, 0);
    lines.push(
      `stalls_3min: ${recent.length} шт., всего ${Math.round(total)} мс, худшее ${Math.round(
        Math.max(...recent.map((s) => s.ms))
      )} мс`
    );
  } else {
    lines.push("stalls_3min: нет");
  }
  return lines;
}
